// src/components/kb/KbVersionView.tsx
//
// Read-only view of a single KB document version (Story 2.2), routed at
// /kb/:documentId/versions/:versionId. Shows the version number, created
// timestamp, source label, and the full content read-only — for ANY version,
// including ones that are no longer latest. This surface is deep-linkable and
// stable: Epic 4's ticket screen links to it so "opening a pinned link opens
// that version, even if the document moved on or was retired". There is no edit,
// no delete, no restore affordance here — versions are immutable and nothing
// disappears.

import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useKbVersion } from '../../hooks/useKbDocuments'
import { formatDateTime } from '../../utils/dateUtils'

export function KbVersionView() {
  const { documentId, versionId } = useParams<{
    documentId: string
    versionId: string
  }>()
  const { version, content, loading, error } = useKbVersion(versionId ?? '')

  const backTo = `/kb/${documentId ?? ''}`

  if (loading) {
    return (
      <div className="space-y-6">
        <BackLink to={backTo} />
        <div className="card p-6">
          <div className="space-y-3" aria-hidden="true">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-6 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error || !version) {
    return (
      <div className="space-y-6">
        <BackLink to={backTo} />
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {error ?? 'This version could not be found.'}
        </div>
      </div>
    )
  }

  const sourceLabel = version.original_filename ?? null

  return (
    <div className="space-y-6">
      <BackLink to={backTo} />

      <div className="card p-6">
        <div className="flex items-start justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            Version {version.version_number}
          </h1>
          <span className="badge badge-gray">Read-only</span>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {'Created '}
          {version.created_at ? formatDateTime(version.created_at) : '—'}
          {sourceLabel ? ` · ${sourceLabel}` : ''}
        </p>
      </div>

      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Content</h2>
        {content ? (
          <pre className="whitespace-pre-wrap break-words text-sm text-gray-700 font-sans">
            {content}
          </pre>
        ) : (
          <p className="text-sm text-gray-500">
            No content is available for this version.
          </p>
        )}
      </div>
    </div>
  )
}

function BackLink({ to }: { to: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
    >
      <ArrowLeft className="w-4 h-4 mr-1" />
      Document
    </Link>
  )
}
